import { useFormik } from "formik";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import {
  CCM_CONSENT_FORM_ID,
  CCM_CONSENT_INITIAL_VALUES,
  CCM_CONSENT_STYLE,
  CCM_CONSENT_TEXT,
  CCM_PRACTICE_CONTACT,
} from "./ccm-consent-constant";
import { ccmConsentSchema } from "./ccm-consent-schema";
import { CcmConsentFormValues, PracticeContact } from "./ccm-consent-types";
import CcmConsentSections from "./components/CcmConsentSections";
import CcmPatientSections from "./components/CcmPatientSections";
import {
  ConsentFormSyncOptions,
  ConsentReadOnlyContext,
  useConsentFormSync,
} from "./hooks/use-consent-form-sync";

export { ccmConsentSchema };

export interface CcmConsentFormProps extends ConsentFormSyncOptions<CcmConsentFormValues> {
  onSubmit?: (values: CcmConsentFormValues) => void | Promise<void>;
  initialValues?: CcmConsentFormValues;
  practiceContact?: PracticeContact;
  /** Lets a footer outside the form submit it via `<button form={formId}>`. */
  formId?: string;
  readOnly?: boolean;
}

const CcmConsentForm = ({
  onSubmit,
  initialValues = CCM_CONSENT_INITIAL_VALUES,
  practiceContact = CCM_PRACTICE_CONTACT,
  formId = CCM_CONSENT_FORM_ID,
  readOnly = false,
  onValuesChange,
  submitCount,
}: CcmConsentFormProps) => {
  const formik = useFormik<CcmConsentFormValues>({
    initialValues,
    validationSchema: ccmConsentSchema,
    enableReinitialize: true,
    onSubmit: (values) => onSubmit?.(values),
  });
  useConsentFormSync(formik, { onValuesChange, submitCount });

  return (
    <ConsentReadOnlyContext.Provider value={readOnly}>
      <form id={formId} noValidate onSubmit={formik.handleSubmit}>
        <Flex flexDirection="column" gap={2}>
          <Text
            component="h2"
            variant="h5"
            textAlign="center"
            fontWeight={CCM_CONSENT_STYLE.HEADING_WEIGHT}
            color={CCM_CONSENT_STYLE.HEADER_TEXT}
          >
            {CCM_CONSENT_TEXT.TITLE}
          </Text>
          <CcmPatientSections
            formik={formik}
            practiceContact={practiceContact}
          />
          <CcmConsentSections
            formik={formik}
            practiceContact={practiceContact}
          />
        </Flex>
      </form>
    </ConsentReadOnlyContext.Provider>
  );
};

export default CcmConsentForm;
